import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, TextField, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, Chip, FormControl,
  InputLabel, Select, MenuItem, CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import PaginatedTable from '../components/common/PaginatedTable';
import { customersApi } from '../api/customers.api';
import { sunatApi } from '../api/sunat.api';
import { useTiposDocumento } from '../hooks/useTiposDocumento';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';

const Customers: React.FC = () => {
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteId, setDeleteId] = useState(0);
  const [filterType, setFilterType] = useState('');

  const [editId, setEditId] = useState<number | null>(null);
  const [type, setType] = useState('persona');
  const [documentType, setDocumentType] = useState('1');
  const [name, setName] = useState('');
  const [document, setDocument] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchMsg, setSearchMsg] = useState<{ kind: 'ok' | 'error' | 'info'; text: string } | null>(null);

  const { tipos, loading: loadingTipos, reload: reloadTipos } = useTiposDocumento();
  const tipoDoc = tipos.find((t) => t.codigo === documentType);

  const load = async () => {
    try {
      const res = await customersApi.getAll(filterType || undefined);
      setCustomers(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterType]);

  const resetForm = () => {
    setType('persona'); setDocumentType('1'); setName(''); setDocument('');
    setPhone(''); setEmail(''); setAddress(''); setEditId(null);
    setSearchMsg(null);
  };

  const consultarDocumento = async (kind: 'DNI' | 'RUC', value: string) => {
    setSearchMsg(null);
    const doc = value.trim();
    if (kind === 'DNI') {
      if (doc.length !== 8) {
        setSearchMsg({ kind: 'error', text: 'El DNI debe tener 8 digitos.' });
        return;
      }
    } else if (doc.length !== 11) {
      setSearchMsg({ kind: 'error', text: 'El RUC debe tener 11 digitos.' });
      return;
    }
    setSearching(true);
    try {
      const res = kind === 'DNI' ? await sunatApi.consultarDni(doc) : await sunatApi.consultarRuc(doc);
      const data = res.data;
      if (data?.success && data.fullName) {
        setName(data.fullName);
        if (kind === 'RUC' && data.address) setAddress(data.address);
        setSearchMsg({ kind: 'ok', text: 'Datos encontrados. Autocompletado correctamente.' });
      } else {
        setSearchMsg({ kind: 'error', text: data?.message || 'No se encontro el documento.' });
      }
    } catch (err: any) {
      setSearchMsg({
        kind: 'error',
        text: err?.response?.data?.message || 'Error consultando el documento.',
      });
    } finally {
      setSearching(false);
    }
  };

  const buscarDocumento = () => {
    if (documentType === '1') return consultarDocumento('DNI', document);
    if (documentType === '6') return consultarDocumento('RUC', document);
    setSearchMsg({ kind: 'info', text: 'Solo se puede buscar DNI o RUC automaticamente.' });
  };

  const handleSave = async () => {
    if (!document.trim()) {
      setSearchMsg({ kind: 'error', text: 'Ingresa el numero de documento.' });
      return;
    }
    try {
      const data = {
        type,
        documentType,
        name,
        document: document.trim(),
        phone,
        email,
        address,
        ruc: documentType === '6' ? document.trim() : '',
        businessName: documentType === '6' ? name : '',
      };
      if (isEditing && editId) {
        await customersApi.update(editId, data);
      } else {
        await customersApi.create(data);
      }
      setModalOpen(false); resetForm(); load();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async () => {
    try {
      await customersApi.delete(deleteId);
      setDeleteDialogOpen(false); load();
    } catch (err) { console.error(err); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Clientes</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Filtrar tipo</InputLabel>
            <Select value={filterType} label="Filtrar tipo"
              onChange={(e) => setFilterType(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="persona">Persona</MenuItem>
              <MenuItem value="empresa">Empresa</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => { resetForm(); setIsEditing(false); reloadTipos(); setModalOpen(true); }}>
            Nuevo Cliente
          </Button>
        </Box>
      </Box>

      <PaginatedTable
        columns={[
          { id: 'id', label: 'ID', minWidth: 60 },
          {
            id: 'type', label: 'Tipo',
            format: (value: string) => (
              <Chip label={value === 'persona' ? 'Persona' : 'Empresa'}
                size="small" color={value === 'persona' ? 'info' : 'warning'} />
            ),
          },
          {
            id: 'documentType', label: 'Tipo Doc',
            format: (value: string) =>
              tipos.find((t) => t.codigo === (value || '1'))?.abreviatura || value || 'DNI',
          },
          { id: 'name', label: 'Nombre', sx: { fontWeight: 500 } },
          { id: 'document', label: 'Documento' },
          { id: 'phone', label: 'Telefono' },
          { id: 'email', label: 'Email' },
        ]}
        rows={customers}
        searchFields={['name', 'document', 'email', 'phone']}
        exportFileName="clientes"
        emptyMessage="No hay clientes registrados"
        renderActions={(c) => (
          <>
            <IconButton size="small" color="primary" onClick={() => {
              const docType = c.documentType || (c.ruc ? '6' : '1');
              setEditId(c.id); setType(c.type); setName(c.name);
              setDocumentType(docType);
              setDocument(docType === '6' ? (c.ruc || c.document) : c.document);
              setPhone(c.phone || '');
              setEmail(c.email || ''); setAddress(c.address || '');
              reloadTipos();
              setIsEditing(true); setSearchMsg(null); setModalOpen(true);
            }}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => {
              setDeleteId(c.id); setDeleteDialogOpen(true);
            }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </>
        )}
      />

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Editar Cliente' : 'Nuevo Cliente'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select value={type} label="Tipo" onChange={(e) => {
                  const value = e.target.value;
                  setType(value);
                  setDocumentType(value === 'empresa' ? '6' : '1');
                  setDocument('');
                  setSearchMsg(null);
                }}>
                  <MenuItem value="persona">Persona Natural</MenuItem>
                  <MenuItem value="empresa">Empresa</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo Documento</InputLabel>
                <Select value={documentType} label="Tipo Documento" disabled={loadingTipos} onChange={(e) => {
                  setDocumentType(e.target.value);
                  setDocument('');
                  setSearchMsg(null);
                }}>
                  {tipos.map((t) => (
                    <MenuItem key={t.codigo} value={t.codigo}>{t.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <TextField fullWidth label={documentType === '6' ? 'Razon Social' : 'Nombre'} value={name}
                onChange={(e) => setName(e.target.value)} required
                autoComplete={documentType === '6' ? 'organization' : 'name'} />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth label={tipoDoc?.nombre || 'Documento'} value={document}
                onChange={(e) => setDocument(e.target.value)} required
                autoComplete="off"
                error={searchMsg?.kind === 'error'}
                helperText={searchMsg?.text}
                slotProps={{
                  htmlInput: { maxLength: tipoDoc?.maxLength || 15 },
                  input: {
                    endAdornment: tipoDoc?.permiteConsulta ? (
                      <IconButton size="small" onClick={buscarDocumento} disabled={searching} title="Buscar en SUNAT">
                        {searching ? <CircularProgress size={16} /> : <SearchIcon />}
                      </IconButton>
                    ) : undefined,
                  },
                }}
              />
            </Grid>
            <Grid size={6}>
              <TextField fullWidth label="Telefono" value={phone}
                onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth label="Email" value={email}
                onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth label="Direccion" value={address}
                onChange={(e) => setAddress(e.target.value)} autoComplete="street-address" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>
            {isEditing ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog open={deleteDialogOpen} title="Eliminar Cliente"
        message="¿Estas seguro de eliminar este cliente?"
        onConfirm={handleDelete} onCancel={() => setDeleteDialogOpen(false)} />
    </Box>
  );
};

export default Customers;
